import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/components/lib/utils"

const buttonVariants = cva(
  // Base styles with enhanced transitions
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold transition-all duration-200 ease-out focus:outline-none focus-visible:outline-none focus-visible:ring-0 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.98]",
  {
    variants: {
      variant: {
        // Primary - Deep navy with subtle gradient
        default:
          "bg-primary text-primary-foreground shadow-md hover:shadow-lg hover:bg-primary/90 relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/0 before:via-white/10 before:to-white/0 before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-500",
        
        // Destructive - Refined red
        destructive:
          "bg-destructive text-destructive-foreground shadow-md hover:shadow-lg hover:bg-destructive/90",
        
        // Outline - Clean border with subtle hover
        outline:
          "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300 shadow-sm",
        
        // Secondary - Soft background
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 hover:shadow-md",
        
        // Ghost - Minimal with hover reveal
        ghost: 
          "text-muted-foreground hover:bg-accent/10 hover:text-accent",
        
        // Link - Underline animation
        link: 
          "text-primary underline-offset-4 hover:underline",
        
        // ===== NEW MODERN VARIANTS =====
        
        // Accent - Clean cyan (primary CTA)
        accent:
          "bg-accent text-white shadow-lg shadow-accent/25 hover:shadow-xl hover:shadow-accent/30 hover:bg-accent/90 active:bg-accent/80",
        
        // Glow - Subtle glow accent for important CTAs
        glow:
          "bg-accent text-white shadow-lg shadow-accent/40 hover:shadow-xl hover:shadow-accent/50 hover:bg-accent/90",
        
        // Glass - Glassmorphism style
        glass:
          "bg-white/10 dark:bg-white/5 backdrop-blur-xl border border-white/20 dark:border-white/10 text-foreground hover:bg-white/20 dark:hover:bg-white/10 shadow-lg",
        
        // Soft - Subtle with brand accent
        soft:
          "bg-accent/10 text-accent hover:bg-accent/20 border border-accent/20",
        
        // Premium - Dark with elegant hover
        premium:
          "bg-gradient-to-br from-[hsl(222,47%,15%)] to-[hsl(222,47%,25%)] text-white shadow-xl hover:shadow-2xl hover:from-[hsl(222,47%,20%)] hover:to-[hsl(222,47%,30%)] border border-white/10",
        
        // Success - Green variant
        success:
          "bg-emerald-500 text-white shadow-md shadow-emerald-500/25 hover:bg-emerald-600 hover:shadow-lg hover:shadow-emerald-500/30",
        
        // Warning - Amber variant
        warning:
          "bg-amber-500 text-white shadow-md shadow-amber-500/25 hover:bg-amber-600 hover:shadow-lg hover:shadow-amber-500/30",
        
        // Info - Teal variant
        info:
          "bg-teal-500 text-white shadow-md shadow-teal-500/25 hover:bg-teal-600 hover:shadow-lg hover:shadow-teal-500/30",
        
        // ===== UNIFIED ACTION BUTTON VARIANTS =====
        
        // Action Primary - Dark slate (for Create, Submit, Add, New, etc.)
        action:
          "bg-slate-800 text-white rounded-full shadow-lg hover:bg-slate-900 hover:shadow-xl transition-all duration-200",
        
        // Action Success - Dark slate (unified with action for consistency)  
        "action-success":
          "bg-slate-800 text-white rounded-full shadow-lg hover:bg-slate-900 hover:shadow-xl transition-all duration-200",
        
        // Action Secondary - White/glass style
        "action-secondary":
          "bg-white/95 backdrop-blur-sm text-slate-700 border border-slate-200/60 rounded-full shadow-lg hover:bg-white hover:shadow-xl transition-all duration-200",
        
        // Filter/Toolbar - For Stats, Filters, toggle buttons in tables
        filter:
          "bg-white border border-slate-200 text-slate-600 rounded-xl shadow-sm hover:bg-slate-50 hover:border-slate-300 hover:text-slate-800 transition-all duration-200",
        
        // Filter Active - When filter/toggle is active
        "filter-active":
          "bg-slate-100 border border-slate-300 text-slate-800 rounded-xl shadow-sm hover:bg-slate-200 transition-all duration-200",

        // ===== PLAYFUL GEOMETRIC (marketing site) =====
        // Only meaningful inside `.theme-playful`, which supplies --pop-ink.

        // Candy - the primary marketing CTA. Pill, chunky ink border, hard
        // shadow that lifts on hover and presses in on click.
        candy:
          "bg-accent text-white font-bold rounded-full border-2 border-pop-ink shadow-pop pop-interactive active:scale-100",

        // Pop outline - secondary action that floods with amber on hover.
        "candy-outline":
          "bg-transparent text-foreground font-bold rounded-full border-2 border-pop-ink hover:bg-pop-amber active:scale-100 transition-colors duration-300",

        // Ink - high contrast dark pill for use on coloured panels.
        "candy-ink":
          "bg-pop-ink text-white font-bold rounded-full border-2 border-pop-ink shadow-pop-soft pop-interactive active:scale-100",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-8 rounded-md px-3.5 text-xs",
        lg: "h-12 rounded-xl px-8 text-base",
        xl: "h-14 rounded-xl px-10 text-lg",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8",
        "icon-lg": "h-12 w-12",
        // Tappable pill sizes for the marketing CTAs (>=48px on mobile).
        pill: "h-12 px-7 text-base",
        "pill-lg": "h-14 px-9 text-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            {children}
          </>
        ) : (
          children
        )}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
